import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteSYS4030_R01, disableSYS4030_R01, downloadExcel_SYS4030_R01, getSYS4030_R01AllCount, getSYS4030_R01List, patchSYS4030_R01, postSYS4030_R01 } from "./Api_SYS4030_R01";
import { defaultSYS4030_R01Filter, SYS4030_R01Filter, useSYS4030_R01Store } from "./Store_SYS4030_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultSYS4030_R01Dto, SYS4030_R01Dto } from "./Dto_SYS4030_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import { validateCustomCd } from "src/utils/grid/GridValidateContent";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	SYS4030_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<SYS4030_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const SYS4030_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		SYS4030_R01Filter, setSYS4030_R01Filter,
		SYS4030_R01PastFilter, setSYS4030_R01PastFilter,
		SYS4030_R01List, setSYS4030_R01List, insertSYS4030_R01List, removeSYS4030_R01List,
		deletedSYS4030_R01List, addDeletedSYS4030_R01List, removeDeletedSYS4030_R01List,
		selectedSYS4030_R01List, setSelectedSYS4030_R01List, removeSelectedSYS4030_R01List,
		originalSYS4030_R01List, setOriginalSYS4030_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useSYS4030_R01Store();

	const { fetchWarehouseCdCode, inoutBoundList, wmsCdList, customCdList, yesNoList, compCdList } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("SYS4030_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	const [clickable, setClickable] = useState(true);

	const defaultSYS4030_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(SYS4030_R01_ROW_VALIDATE, {
				compCdList,
				inoutBoundList,
				wmsCdList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultSYS4030_R01Dto };
		}

		return {
			...defaultSYS4030_R01Dto,
			compCd: compCdList[0].compCd,
			inoutType: inoutBoundList[0].minorCd,
			logicCenter: wmsCdList[0].minorCd,
			approveFlg: yesNoList.find(value => value.minorCd === 'N')?.minorCd || 'N',
			inserted: true,
		};
	}, [defaultSYS4030_R01Dto, compCdList, inoutBoundList, wmsCdList, validateCommonCodeLists]);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedSYS4030_R01List.length === 0 && selectedSYS4030_R01List.length === 0,
			save: SYS4030_R01List.length === 0,
			print: SYS4030_R01List.length === 0,
			excel: SYS4030_R01List.length === 0
		}));
	}, [selectedSYS4030_R01List, deletedSYS4030_R01List, SYS4030_R01List]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_SYS4030_R01(
			'',
			getParams(SYS4030_R01Filter),
			SYS4030_R01List.length
		);

		downloadSettingExcel({ responseData: response.data.data, fileName: '창고 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [SYS4030_R01Filter, SYS4030_R01List]);

	/**
	 * 사용자명 입력 변경 핸들러
	 * 검색어 입력 시 호출
	 */
	const handleorgNmChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSYS4030_R01Filter({...SYS4030_R01Filter, useYn: e.target.value});
	}

	const validateInsertData = (data: SYS4030_R01Dto, currentRowIndex: number) => {
		if(!validateString(data.warehouseCd)) {
			showWarning('창고코드를 입력해주세요.', 3000); return false;
		}
		if(!validateString(data.warehouseNm)) { 
			showWarning('창고명을 입력해주세요.\n창고코드 : ' + data.warehouseCd, 3000); return false;
		}
		if( data.inoutType?.includes('INBOUND') && validateString(data.customCd) ) {
			showWarning('내부창고에는 거래처코드를 입력할수 없습니다. \n창고코드 : ' + data.warehouseCd, 3000); return false;
		}
		if(!validateString(data.customCd) && !data.inoutType?.includes('INBOUND')) {
			showWarning('거래처코드를 입력해주세요. - ' + data.warehouseNm); return false;
		}
		if (!validateCustomCd(data.customCd, customCdList) && !data.inoutType?.includes('INBOUND')) {
			showWarning('거래처가 존재하지 않습니다.(입력칸을 더블클릭하거나 엔터키로 세부사항을 열어서 지정해주세요)'); return false;
		}

		// 1. 삭제하기로 표시된 사용자들을 먼저 제외
		const activeUsers = SYS4030_R01List.filter(user => !deletedSYS4030_R01List.includes(user.warehouseCd));
		// 현재 검증 중인 행의 인덱스를 제외한 나머지 행들
		const otherUsers = activeUsers.filter((_, index) => index !== currentRowIndex);
		
		if (otherUsers.some(user => user.warehouseCd === data.warehouseCd && user.compCd === data.compCd)) {
			showWarning("중복된 창고코드가 있습니다.\n창고코드: " + data.warehouseCd);
			return false;
		}

		return true;
	}

	const validateWarehouseAuthData = (list: SYS4030_R01Dto[]) => {
		const hasYn = (key: keyof SYS4030_R01Dto) =>
			list.some(user => user[key] !== 'N' && user[key] !== '' && user[key] !== null && user[key] !== undefined);

		if (!hasYn('materialYn')) {
			showWarning('자재창고권한을 가진 창고가 하나 이상 있어야 합니다.');
			return false;
		}
		if (!hasYn('goodsYn')) {
			showWarning('상품창고권한을 가진 창고가 하나 이상 있어야 합니다.');
			return false;
		}
		if (!hasYn('productYn')) {
			showWarning('제품창고권한을 가진 창고가 하나 이상 있어야 합니다.');
			return false;
		}
		if (!hasYn('manufactYn')) {
			showWarning('공정창고권한을 가진 창고가 하나 이상 있어야 합니다.');
			return false;
		}
		if (!hasYn('badYn')) {
			showWarning('불량창고권한을 가진 창고가 하나 이상 있어야 합니다.');
			return false;
		}
		return true;
	}

	const getParams = (filter: SYS4030_R01Filter) => {
		const params: Partial<SYS4030_R01Filter> = {
			compCd: filter.compCd || undefined,
			logicCenter: filter.logicCenter || undefined,
			inoutType: filter.inoutType || undefined,
			useYn: filter.useYn || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS4030_R01AllCount(getParams(SYS4030_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS4030_R01Filter, setAllCount]);


	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}
	
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setSYS4030_R01List([]);
		}

		const responseData = await getSYS4030_R01List(	
			'',
			getParams(SYS4030_R01Filter),
			SYS4030_R01List.length ?? 0
		);
		let newList = [...responseData.data.data ]
		setSYS4030_R01List(newList);
	
		const processAllCount = await renewalAllCount();
		setSYS4030_R01List(newList);
		setOriginalSYS4030_R01List(newList);
		setSelectedSYS4030_R01List([]);
		removeDeletedSYS4030_R01List(deletedSYS4030_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');
	
		if (newList.length === 0) {
			// insertSYS4030_R01List([{ ...defaultSYS4030_R01Row, inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else if (messageMode === 'show') {
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		}
	
		setSYS4030_R01PastFilter(SYS4030_R01Filter);
		setLoadingSpinner(false);
	}, [SYS4030_R01Filter, SYS4030_R01PastFilter, SYS4030_R01List, deletedSYS4030_R01List, gridRef, setMessage, setLoadingSpinner, setOriginalSYS4030_R01List, setSelectedSYS4030_R01List, insertSYS4030_R01List, renewalAllCount, setScrollTop]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch]);
	
	
	const initContainerData = useCallback(async () => {
		initData();
		setMessage("");
	}, [initData, setMessage]);

	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: SYS4030_R01List,
			rowData: {...defaultSYS4030_R01Row(), valueIndex: -1},
			insertRow: insertSYS4030_R01List,
			defaultRowData: defaultSYS4030_R01Row(),
			setClickable
		});
	}, [SYS4030_R01List, insertSYS4030_R01List, setClickable]);
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리
		const insertedList: SYS4030_R01Dto[] = SYS4030_R01List.filter(row => 
			!deletedSYS4030_R01List.includes(row.warehouseCd) && 
			row.inserted === true
		).map(row => ({ ...row, }));

		// 수정된 사용자만 필터링
		const modifiedList = SYS4030_R01List.filter(row => 
			!deletedSYS4030_R01List.includes(row.warehouseCd) && 
			row.isModified && !row.inserted
		).map(row => ({ ...row, }));
		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = SYS4030_R01List.findIndex(user => user.warehouseCd === value.warehouseCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.warehouseCd);
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();	
		valid = true;
		insertedList.forEach((value) => {
			let index = SYS4030_R01List.findIndex(user => user.warehouseCd === value.warehouseCd);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.warehouseCd);
		});
		if(!valid) { return; }

		const activeList = SYS4030_R01List.filter(row => !deletedSYS4030_R01List.includes(row.warehouseCd));
		if (!validateWarehouseAuthData(activeList)) { return; }

		const deleteStringList = deletedSYS4030_R01List.map(item => item.toString());
		const deleteTargetList = SYS4030_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postSYS4030_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchSYS4030_R01(modifiedList) : Promise.resolve(),
			deleteTargetList.length > 0 ? Promise.all(deleteTargetList.map(value =>  deleteSYS4030_R01(value.warehouseCd) )) : Promise.resolve()
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('창고정보 저장이 완료되었습니다.');
			setMessage('창고정보 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('창고정보 삭제가 완료되었습니다.');
			setMessage('창고정보 삭제가 완료되었습니다.');
		}

		setTimeout(() => { setMessage(''); }, 5000);
		fetchWarehouseCdCode();
		await executeDataFetch('refetch', { messageMode: 'none' });
	}, [SYS4030_R01List, deletedSYS4030_R01List]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedSYS4030_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedSYS4030_R01List(deletedSYS4030_R01List);
			removeSelectedSYS4030_R01List();
			setSYS4030_R01List([...SYS4030_R01List]);
		} else if (selectedSYS4030_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedSYS4030_R01List(selectedSYS4030_R01List);
			removeSelectedSYS4030_R01List(selectedSYS4030_R01List);
		}
	}, [selectedSYS4030_R01List, deletedSYS4030_R01List, addDeletedSYS4030_R01List, removeDeletedSYS4030_R01List, removeSelectedSYS4030_R01List, setSYS4030_R01List, SYS4030_R01List]);

	return{
		clickable, setClickable,
		defaultSYS4030_R01Row,
		handleExcelClick,
		handleorgNmChange,
		fetchData,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		buttonDisabled,
	}
}
